import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Medal, Timer, TrendingUp, Trophy, Users, Wrench } from 'lucide-react';
import { useI18n } from '../../i18n';
import { APP_PREFERENCE_KEYS, useAppPreference } from '../../utils/appPreferences';
import { remaining, useCountdown } from '../kartlife/launchClock';
import { enterCard } from './chooserEntry';
import './appChooser.css';
import './chooserLaunch.css';

// The premiere's own words on the KartLife card, until the countdown ends.
const LAUNCH = {
  pl: { badge: 'PREMIERA', line: 'Jeszcze chwila. Potem Twój ruch.', date: '1 listopada 2026 · 13:00 · czas polski', units: ['dni', 'godz.', 'min', 'sek.'], timer: 'Do premiery KartLife' },
  en: { badge: 'LAUNCH', line: 'Almost time. Then it’s your move.', date: '1 November 2026 · 13:00 · Warsaw time', units: ['days', 'hrs', 'min', 'sec'], timer: 'Until KartLife launches' },
  de: { badge: 'START', line: 'Bald ist es so weit. Dann bist du dran.', date: '1. November 2026 · 13:00 · Warschauer Zeit', units: ['Tage', 'Std.', 'Min.', 'Sek.'], timer: 'Bis zum KartLife-Start' },
  sk: { badge: 'PREMIÉRA', line: 'Už len chvíľu. Potom je rad na tebe.', date: '1. novembra 2026 · 13:00 · poľský čas', units: ['dní', 'hod.', 'min', 'sek.'], timer: 'Do premiéry KartLife' },
  cs: { badge: 'PREMIÉRA', line: 'Už jen chvíli. Pak je řada na tobě.', date: '1. listopadu 2026 · 13:00 · polský čas', units: ['dní', 'hod.', 'min', 'sek.'], timer: 'Do premiéry KartLife' },
};

function Clock({ left, copy }) {
  const { days, hours, minutes, seconds } = remaining(left);
  return <div className="app-chooser__clock" role="timer" aria-label={copy.timer} aria-live="off">
    {[days, hours, minutes, seconds].map((value, index) => <div key={copy.units[index]}>
      <strong>{String(value).padStart(2, '0')}</strong><span>{copy.units[index]}</span>
    </div>)}
  </div>;
}

// Slogans that lean out from behind a hovered card: the first two over its top edge, the other two under its bottom edge.
// KartLife uses the icons of the game's own navigation.
const PEEKS = {
  mykart: [
    { icon: Timer, text: 'Zapisuj czasy okrążeń' },
    { icon: TrendingUp, text: 'Bij własne rekordy' },
    { icon: Medal, text: 'Rywalizuj w rankingach' },
    { icon: Users, text: 'Porównuj się ze znajomymi' },
  ],
  kartlife: [
    { icon: Wrench, text: 'Ulepszaj swój gokart' },
    { icon: Timer, text: 'Trenuj kierowcę' },
    { icon: Trophy, text: 'Wygrywaj wyścigi w lidze' },
    { icon: Users, text: 'Graj w drużynie' },
  ],
};

function Peeks({ items, t }) {
  const peek = ({ icon: Icon, text }) => (
    <span key={text} className="app-chooser__peek">
      <span className="app-chooser__peek-icon"><Icon size={14} strokeWidth={2} /></span>
      <span>{t(text)}</span>
    </span>
  );
  return (
    <span className="app-chooser__peeks" aria-hidden="true">
      <span className="app-chooser__peek-row app-chooser__peek-row--top">{items.slice(0, 2).map(peek)}</span>
      <span className="app-chooser__peek-row app-chooser__peek-row--bottom">{items.slice(2).map(peek)}</span>
    </span>
  );
}

export default function AppChooser({ preloadApp }) {
  const { t, language } = useI18n();
  const left = useCountdown();
  const premiere = left > 0;
  const launch = LAUNCH[language] || LAUNCH.en;
  const navigate = useNavigate();
  const reduceMotion = useAppPreference(APP_PREFERENCE_KEYS.reduceMotion);
  // Both destinations live in the main app, so fetch it while the person decides.
  useEffect(() => { preloadApp?.().catch(() => {}); }, [preloadApp]);
  const enter = to => event => enterCard(event, reduceMotion, () => navigate(to));
  return (
    <main className={`app-chooser${reduceMotion ? ' mykart-reduce-motion' : ''}`} aria-label={t('Wybierz, dokąd chcesz przejść')}>
      <div className="app-chooser__ambience" aria-hidden="true">
        {/* Faint glimpses of each app, shown while its card is hovered. The image is set as a variable so phones never download it. */}
        <span className="app-chooser__preview app-chooser__preview--mykart" style={{ '--preview-image': 'url(/screenshots/desktop.webp)' }} />
        <span className="app-chooser__preview app-chooser__preview--kartlife" style={{ '--preview-image': premiere ? 'url(/kartlife/promo/premiere-teaser.webp)' : 'url(/kartlife/promo/garage.webp)' }} />
        <span className="app-chooser__aurora app-chooser__aurora--green" />
        <span className="app-chooser__aurora app-chooser__aurora--orange" />
        <span className="app-chooser__particles app-chooser__particles--green" />
        <span className="app-chooser__particles app-chooser__particles--orange" />
      </div>
      <div className="app-chooser__content">
      <header className="app-chooser__intro">
        <p>{t('Dwa światy kartingu')}</p>
        <h1>{t('Dokąd dziś?')}</h1>
      </header>
      <div className="app-chooser__grid">
        <Link to="/aktualnosci" className="app-chooser__card app-chooser__card--mykart" onClick={enter('/aktualnosci')}>
          <div className="app-chooser__surface" style={{ '--card-art': 'url(/screenshots/desktop.webp)' }}>
            <span className="app-chooser__mark" aria-hidden="true">
              <img src="/logo_black.webp" alt="" />
            </span>
            <div className="app-chooser__copy">
              <h2 className="app-chooser__brand" aria-label="MyKart">MyKart<span aria-hidden="true">.</span></h2>
              <p>{t('Twój osobisty dziennik czasów.')}</p>
            </div>
            <span className="app-chooser__arrow" aria-hidden="true"><ArrowUpRight size={23} strokeWidth={1.5} /></span>
          </div>
          <Peeks items={PEEKS.mykart} t={t} />
        </Link>
        <Link to="/kartlife" className={`app-chooser__card app-chooser__card--kartlife${premiere ? ' app-chooser__card--premiere' : ''}`} onClick={enter('/kartlife')}>
          <div className="app-chooser__surface" style={{ '--card-art': 'url(/kartlife/promo/premiere-teaser.webp)' }}>
            <span className="app-chooser__badge">{premiere ? launch.badge : t('NOWOŚĆ')}</span>
            <span className="app-chooser__mark app-chooser__mark--game" aria-hidden="true">
              <img src="/images/kartlife/brand/kartlife-mark-kl-v2.png" alt="" />
            </span>
            <div className="app-chooser__copy">
              <h2 className="app-chooser__brand" aria-label="KartLife">Kart<span>Life</span></h2>
              <p>{premiere ? launch.line : t('Twój gokart. Twoja droga na szczyt.')}</p>
            </div>
            {premiere
              ? <div className="app-chooser__launch-details"><p className="app-chooser__launch-date">{launch.date}</p><Clock left={left} copy={launch} /></div>
              : <span className="app-chooser__arrow" aria-hidden="true"><ArrowUpRight size={23} strokeWidth={1.5} /></span>}
          </div>
          <Peeks items={PEEKS.kartlife} t={t} />
        </Link>
      </div>
      </div>
    </main>
  );
}
